import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowUpCircle, ArrowDownCircle, Zap, Lock, Unlock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradingSimulator = ({ currentPrice, onTrade }) => {
  const [tradeType, setTradeType] = useState('buy');
  const [amount, setAmount] = useState('');
  const [autoSellPrice, setAutoSellPrice] = useState('');
  const [freezePrice, setFreezePrice] = useState('');
  const [trading, setTrading] = useState(false);

  const executeTrade = async () => {
    if (MAINTENANCE_MODE) {
      toast.error('Trading Unavailable', {
        description: 'Trading is temporarily disabled during maintenance.'
      });
      return;
    }
    
    if (!amount || !currentPrice) {
      toast.error('Please enter an amount');
      return;
    }

    setTrading(true);
    try {
      await axios.post(`${API}/trades`, {
        type: tradeType,
        amount: parseFloat(amount),
        price: currentPrice
      });

      if (tradeType === 'buy') {
        await axios.post(`${API}/portfolio`, {
          amount: parseFloat(amount),
          purchase_price: currentPrice,
          notes: `Simulated ${tradeType} trade`
        });
      }

      toast.success(`${tradeType === 'buy' ? 'Buy' : 'Sell'} order executed!`, {
        description: `${amount} BTC at $${currentPrice.toLocaleString()}`
      });
      setAmount('');
      onTrade();
    } catch (error) {
      console.error('Error executing trade:', error);
      toast.error(error.response?.data?.detail || 'Trade failed');
    } finally {
      setTrading(false);
    }
  };

  const setAutoRules = () => {
    if (MAINTENANCE_MODE) {
      toast.error('Trading Unavailable', {
        description: 'Trading rules are temporarily disabled during maintenance.'
      });
      return;
    }
    
    if (autoSellPrice) {
      toast.success('Auto-sell activated', {
        description: `Will sell when price reaches $${parseFloat(autoSellPrice).toLocaleString()}`
      });
    }
    if (freezePrice) {
      toast.success('Freeze rule set', {
        description: `Trading will freeze if price drops below $${parseFloat(freezePrice).toLocaleString()}`
      });
    }
  };

  return (
    <Card className="p-6 h-full border-2 border-dashed border-violet-200 dark:border-violet-900/30">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-heading font-semibold tracking-tight">Trading Simulator</h3>
          <p className="text-xs text-muted-foreground">Paper trading (not real)</p>
        </div>
      </div>

      <Tabs value={tradeType} onValueChange={(v) => !MAINTENANCE_MODE && setTradeType(v)} className="mb-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger 
            data-testid="buy-tab" 
            value="buy" 
            disabled={MAINTENANCE_MODE}
            className="data-[state=active]:bg-gain data-[state=active]:text-white disabled:opacity-50"
          >
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            Buy
          </TabsTrigger>
          <TabsTrigger 
            data-testid="sell-tab" 
            value="sell" 
            disabled={MAINTENANCE_MODE}
            className="data-[state=active]:bg-loss data-[state=active]:text-white disabled:opacity-50"
          >
            <ArrowDownCircle className="w-4 h-4 mr-2" />
            Sell
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Maintenance Notice */}
      {MAINTENANCE_MODE && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span className="text-amber-400">Trading is temporarily disabled during maintenance.</span>
        </div>
      )}

      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm font-medium mb-2 block">Amount (BTC)</label>
          <Input
            data-testid="trade-amount-input"
            type="number"
            step="0.00000001"
            placeholder="0.001"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={MAINTENANCE_MODE}
            className="disabled:opacity-50"
          />
          {amount && currentPrice && (
            <p className="text-xs text-muted-foreground mt-1">
              ≈ ${(parseFloat(amount) * currentPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </div>

        <Button
          data-testid="execute-trade-btn"
          onClick={executeTrade}
          disabled={trading || MAINTENANCE_MODE}
          className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${tradeType === 'buy' ? 'bg-gain hover:bg-gain/90' : 'bg-loss hover:bg-loss/90'}`}
        >
          {MAINTENANCE_MODE ? 'Trading Unavailable' : trading ? 'Processing...' : `${tradeType === 'buy' ? 'Buy' : 'Sell'} BTC`}
        </Button>
      </div>

      <div className="pt-6 border-t border-border space-y-4">
        <h4 className="text-sm font-medium">Auto Trading Rules</h4>
        
        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <Unlock className="w-4 h-4 text-gain" />
            Auto-Sell at Gain Price
          </label>
          <Input
            data-testid="auto-sell-price-input"
            type="number"
            step="100"
            placeholder="Target price"
            value={autoSellPrice}
            onChange={(e) => setAutoSellPrice(e.target.value)}
            disabled={MAINTENANCE_MODE}
            className="disabled:opacity-50"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <Lock className="w-4 h-4 text-loss" />
            Freeze at Loss Price
          </label>
          <Input
            data-testid="freeze-price-input"
            type="number"
            step="100"
            placeholder="Stop-loss price"
            value={freezePrice}
            onChange={(e) => setFreezePrice(e.target.value)}
            disabled={MAINTENANCE_MODE}
            className="disabled:opacity-50"
          />
        </div>

        <Button
          data-testid="set-auto-rules-btn"
          onClick={setAutoRules}
          variant="outline"
          className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={MAINTENANCE_MODE || (!autoSellPrice && !freezePrice)}
        >
          {MAINTENANCE_MODE ? 'Auto Rules Unavailable' : 'Set Auto Rules'}
        </Button>
      </div>
    </Card>
  );
};

export default TradingSimulator;